import { Model, Factory, belongsTo } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import { rand, randPastDate } from '@ngneat/falso'

import type { SchemaAttributes } from '../types.js'
import type { MockServer } from './server.js'
import { PatientCPRStatusValues } from '@/composables/usePatientDetails.js'
import { createErrorResponse, getExistingEntity } from './utils.js'
import { getTodayDateTimeISO } from '@/utils/date/getter.js'

export type PatientCPRStatusEntryData =
  SchemaAttributes<'PatientCPRStatusEntry'>

export const model = (
  Model as ModelDefinition<PatientCPRStatusEntryData>
).extend({
  patient: belongsTo('patient'),
  consultation: belongsTo('consultation'),
  createdUser: belongsTo('user'),
})

export const factory = Factory.extend<PatientCPRStatusEntryData>({
  cpr_status: () => rand(Object.values(PatientCPRStatusValues)),
  created: () => randPastDate().toISOString(),
})

export function routes(server: MockServer) {
  server.resource(
    /**
     * MirageJS internally uses `patientCprStatusEntry` as the
     * camel-case version for `PatientCPRStatusEntry`
     */
    'patientCprStatusEntry',
    { path: '/patient_cpr_statuses' },
  )

  server.post('/patient_cpr_statuses', function (schema, request) {
    const requestBody = JSON.parse(request.requestBody)
    const attributes = requestBody.data.attributes
    const relationships = requestBody.data.relationships

    if (!relationships?.patient?.data?.id) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'Missing "patient" relationship' }],
      })
    }

    const patient = schema.find('patient', relationships.patient.data.id)
    if (!patient) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'Patient not found' }],
      })
    }

    const createdUser = getExistingEntity(server, 'user')

    if (relationships?.consultation?.data?.id) {
      const consultation = schema.find(
        'consultation',
        relationships.consultation.data.id,
      )
      if (!consultation) {
        return createErrorResponse({
          status: 400,
          errors: [{ detail: 'Consultation not found' }],
        })
      }
    }

    const cprStatusEntry = schema.create('patientCprStatusEntry', {
      cpr_status: attributes.cpr_status,
      created: getTodayDateTimeISO(),
      patientId: relationships.patient.data.id,
      createdUser,
      ...(relationships?.consultation?.data?.id && {
        consultationId: relationships.consultation.data.id,
      }),
    })

    // Update patient's cprStatus relationship
    patient.update({ cprStatus: cprStatusEntry })

    return cprStatusEntry
  })
}
